import React, { useCallback, useEffect } from 'react'
import { FlatList } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { Stack } from '@zet/design-system'
import { KeyBoardWrapper } from '@zet/utils'
import { Banner } from '@zet/zet-pro-quick-journey'
import { useAddCustomerState } from '@zet/zet-pro-quick-journey/hooks/useAddCustomer'
import useGetAgentDetailsAndTheme from '@zet/zet-pro-quick-journey/hooks/useGetAgentDetailsAndTheme'
import { useForm } from 'react-hook-form'

import { Button, ScreenTitle } from '../../components'
import FormController from '../../form/FormController'
import { sendAnalyticsData } from '../../utils/analyticsEvents'
import { customerInfoValidationSchema } from '../../utils/validationSchemas'
import { customerInfoFormFields } from './formFields'

export const CustomerInfo = () => {
  const { mutate, isLoading: isAddingCustomer } = useAddCustomerState()
  const { bannerImage } = useGetAgentDetailsAndTheme()
  const { setCustomerPhoneNumber, customerPhoneNumber } = useAddCustomerStore()

  const {
    control,
    formState: { isValid },
    getValues,
    setValue,
  } = useForm({
    mode: 'onTouched',
    resolver: zodResolver(customerInfoValidationSchema),
  })

  const renderItem = useCallback(({ item }) => {
    return <FormController control={control} ocField={item} />
  }, [])

  const handleSubmit = () => {
    sendAnalyticsData({
      screen: 'SHOW_PRIMARY_DETAILS_SCREEN_JFS',
      type: 'click',
    })

    const { fullName, email, phoneNumber } = getValues()
    setCustomerPhoneNumber(phoneNumber)
    const urlParams = new URLSearchParams(window.location.search)
    const hash = encodeURIComponent(urlParams.get('hash') || '')
    const customerId = sessionStorage.getItem('customerId')
    const jfsConsent = sessionStorage.getItem('jfsConsent') as string
    const requestBodyData = {
      hash,
      email: email?.trim(),
      fullName: fullName?.trim(),
      phoneNumber: phoneNumber,
      ...(customerId && { customerId: Number(customerId) }),
      jfsConsent: JSON.parse(jfsConsent),
      screenName: 'SHOWN_PRIMARY_DETAILS_SCREEN_JFS',
    }
    mutate(requestBodyData, {
      onSuccess: (data) => {
        sendAnalyticsData({ screen: data?.action, type: 'screen' })
      },
    })
  }

  useEffect(() => {
    const getPhoneNumberFromUrl = () => {
      const params = new URLSearchParams(window.location.search)
      return params.get('phoneNumber')
    }

    const phoneNumber = customerPhoneNumber || getPhoneNumberFromUrl()

    if (phoneNumber) {
      setValue('phoneNumber', phoneNumber)
    }
  }, [customerPhoneNumber, setValue])

  return (
    <KeyBoardWrapper
      BottomSheet={
        <Button
          disabled={!isValid || isAddingCustomer}
          onPress={handleSubmit}
          color="bg-primary-100"
          title={'Continue'}
          loading={isAddingCustomer}
        />
      }
    >
      <Stack className="flex-1 bg-white px-3">
        <Banner img={bannerImage} />
        <Stack>
          <FlatList
            showsVerticalScrollIndicator={false}
            data={customerInfoFormFields}
            nestedScrollEnabled
            ListHeaderComponent={
              <ScreenTitle
                title={'Provide your details'}
                subTitle={'Please add your details to check your eligibility.'}
              />
            }
            bounces={false}
            renderItem={renderItem}
          />
        </Stack>
      </Stack>
    </KeyBoardWrapper>
  )
}
